import type {
    OnboardingApplicationStatus,
    OnboardingApplicationView,
    OnboardingStepStatus,
    OnboardingStepView
} from "@/services/clientOnboarding";

import { baselineSubsections } from "./mockScenarios";
import {
    beneficialOwnershipEntryCount,
    isStepEffectivelyComplete,
    slotStatusFromPayload,
    STEP_TO_SUBSECTION
} from "./stepReadiness";
import type {
    ApplicationState,
    BannerSpec,
    OnboardingScenario,
    OnboardingSubsection,
    SectionStatus,
    Slot,
    SubsectionId
} from "./types";

/** Copy for the rail's locked agreements. No em dashes. */
const AGREEMENT_LOCK_LABEL = "Unlocks once Solomon has reviewed your application";

const AGREEMENTS_UNLOCKED_STATUSES: ReadonlySet<OnboardingApplicationStatus> = new Set([
    "awaiting_agreements",
    "approved"
]);

/**
 * Review and submit is the applicant's last stop before filing, so its badge
 * answers "can I submit yet?" rather than mirroring a step: it is done once
 * the application is submitted, ready when every applicant section is, and
 * not started while anything above it is outstanding.
 */
function reviewSectionStatus(submitted: boolean, statusBySubsection: Map<SubsectionId, SectionStatus>): SectionStatus {
    if (submitted) return "Complete";
    const applicantSections = [...statusBySubsection.entries()].filter(
        ([id]) => id !== "msa" && id !== "order-form" && id !== "sanctions-screening"
    );
    const everySectionDone =
        applicantSections.length > 0 && applicantSections.every(([, status]) => status === "Complete");
    return everySectionDone ? "WaitingOnYou" : "NotStarted";
}

function stepStatusToSectionStatus(s: OnboardingStepStatus): SectionStatus {
    switch (s) {
        case "not_started":
            return "NotStarted";
        case "in_progress":
            return "InProgress";
        case "pending_external":
            return "WaitingOnSolomon";
        case "complete":
            return "Complete";
        case "rejected":
            return "NeedsAttention";
    }
}

function applicationStateFromStatuses(
    appStatus: OnboardingApplicationStatus,
    steps: OnboardingStepView[]
): ApplicationState {
    if (appStatus === "in_review") return "UnderReview";
    if (appStatus === "awaiting_agreements") return "AwaitingAgreements";
    if (appStatus === "approved") return "Approved";
    if (appStatus === "rejected") return "Declined";
    // draft — check if any step is in progress; otherwise NotStarted.
    const anyMoved = steps.some((s) => s.status !== "not_started" && s.stepName !== "sanctions_screening");
    return anyMoved ? "InProgress" : "NotStarted";
}

function bannerFor(
    state: ApplicationState,
    sla: string,
    signatoryEmail: string | null,
    nextActionableId?: SubsectionId
): BannerSpec {
    switch (state) {
        case "Approved":
            return { kind: "approved", headline: "You're live with YaaS." };
        case "AwaitingAgreements":
            // No CTA: the next action is in the signatory's inbox, not on
            // this page, and the agreements' own rail rows are where the
            // state is tracked.
            return {
                kind: "agreements_out",
                headline: signatoryEmail
                    ? `Your application is approved. Sign the agreements from the DocuSign email sent to ${signatoryEmail} to go live.`
                    : "Your application is approved. Sign the agreements from the DocuSign email to go live."
            };
        case "Declined":
            return {
                kind: "action_required",
                headline: "Application declined. Please contact our compliance team.",
                ctaLabel: "Contact compliance"
            };
        case "UnderReview":
        case "InformationRequested":
            return {
                kind: "we_are_reviewing",
                headline: `We're reviewing. Typically ${sla}; we'll email you when there's an update.`
            };
        case "NotStarted":
            return {
                kind: "your_turn",
                headline: "Your turn. Start with Use Case to unlock the rest.",
                ctaLabel: "Start with Use Case",
                ctaTargetId: "use-case"
            };
        case "InProgress":
            return {
                kind: "your_turn",
                headline: nextActionableId ? "Your turn. Continue where you left off." : "Your turn.",
                ctaLabel: "Continue",
                ctaTargetId: nextActionableId
            };
    }
}

/**
 * Convert a backend application bundle into the FE render-side
 * `OnboardingScenario` so the existing renderer (subsection panels,
 * status badges, slot numbering, celebration animation) stays
 * unchanged. The baseline copy (titles, descriptions, slot examples)
 * comes from `baselineSubsections()`; we overlay the live `status`
 * onto each matching subsection.
 */
export function scenarioFromServer(args: {
    application: OnboardingApplicationView;
    steps: OnboardingStepView[];
    orgName: string;
    sla: string;
}): OnboardingScenario {
    const { application, steps, orgName, sla } = args;
    const state = applicationStateFromStatuses(application.status, steps);

    // Map step → status overlay by subsection id.
    const statusBySubsection = new Map<SubsectionId, SectionStatus>();
    // Map step → per-slot completion overlay. A section badge says the
    // section is unfinished; these say which sub-row is the one missing.
    const payloadBySubsection = new Map<SubsectionId, Record<string, unknown> | null>();
    for (const step of steps) {
        const subsectionId = STEP_TO_SUBSECTION[step.stepName];
        if (subsectionId === undefined) continue;
        let sectionStatus = stepStatusToSectionStatus(step.status);
        if (
            step.stepName === "legal_entity" &&
            application.status === "draft" &&
            step.status === "complete" &&
            !isStepEffectivelyComplete(step)
        ) {
            sectionStatus = "InProgress";
        }
        if (step.stepName === "beneficial_ownership" && beneficialOwnershipEntryCount(step.payload) === 0) {
            sectionStatus = "NotStarted";
        }
        statusBySubsection.set(subsectionId, sectionStatus);
        payloadBySubsection.set(subsectionId, step.payload);
    }

    // No cross-step rollups here: this hub renders Legal Entity (§2) and
    // Tax ID (§3) as separate rail rows, so each row mirrors its own BE
    // step verbatim. The Company-bundle rollup
    // (rollupCompanySubsectionStatus) came from the retired Jayesh design,
    // which hid the tax row and hosted both forms on one card; applying
    // it here made a completed Legal Entity read as "Not Started" while
    // Tax ID was untouched. The MSA precondition gate still uses the
    // rollup (see stepReadiness) to present the pair as one gate item.

    const hasMintingAgreement = steps.some((step) => step.stepName === "minting_agreement_signing");
    const submitted = application.status !== "draft";
    // Before approval the agreements are not the client's to start: they are
    // sent by the issuer's review verdict. The rail shows them locked with
    // that reason rather than as work the applicant is failing to do.
    const agreementsLocked = !AGREEMENTS_UNLOCKED_STATUSES.has(application.status);
    const subsections: OnboardingSubsection[] = baselineSubsections()
        .filter((sub) => sub.id !== "order-form" || hasMintingAgreement)
        .map((sub) => {
            if (sub.id === "review-submit") {
                return { ...sub, status: reviewSectionStatus(submitted, statusBySubsection) };
            }
            const isAgreement = sub.id === "msa" || sub.id === "order-form";
            const liveStatus = statusBySubsection.get(sub.id);
            if (liveStatus === undefined) {
                // The lock is a fact about the application, not about the
                // step row, so it applies even to a bundle that has not
                // composed the signing step in yet.
                return agreementsLocked && isAgreement
                    ? { ...sub, prerequisiteLabel: AGREEMENT_LOCK_LABEL, estimateLabel: undefined }
                    : sub;
            }
            const slots: Slot[] = sub.slots.map((slot) => {
                const slotStatus = slotStatusFromPayload(slot, payloadBySubsection.get(sub.id));
                return slotStatus === undefined ? slot : { ...slot, status: slotStatus };
            });
            // Live state takes over; clear readiness-hint / estimate for
            // post-`NotStarted` states so the rendered chrome matches reality.
            const next: OnboardingSubsection = { ...sub, status: liveStatus, slots };
            if (liveStatus !== "NotStarted") {
                next.prerequisiteLabel = undefined;
            }
            if (liveStatus === "Complete") {
                next.readinessHint = undefined;
                next.estimateLabel = undefined;
            }
            if (agreementsLocked && isAgreement) {
                next.prerequisiteLabel = AGREEMENT_LOCK_LABEL;
                next.estimateLabel = undefined;
            }
            return next;
        });

    // First non-complete subsection — drives the InProgress banner CTA. The
    // locked agreements are skipped: pointing the applicant at work they
    // cannot start is worse than pointing at nothing.
    const nextActionable = subsections.find(
        (s) =>
            s.treatment !== "solomon-run" &&
            s.treatment !== "deferred" &&
            s.isApplicable &&
            s.prerequisiteLabel === undefined &&
            s.status !== "Complete"
    );

    const lifecycle: OnboardingScenario["lifecycle"] = {
        started: new Date(application.createdAt).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric"
        })
    };
    if (application.submittedAt) {
        lifecycle.submitted = new Date(application.submittedAt).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric"
        });
    }

    return {
        name: "live",
        state,
        // An explicit null is the first approved visit. The hub keeps this
        // celebration mounted while it records the acknowledgement; a set
        // timestamp means a later manual visit and should not replay it.
        celebrate: state === "Approved" && application.approvalAcknowledgedAt === null,
        orgName,
        sla,
        lifecycle,
        banner: bannerFor(state, sla, application.signatoryEmail, nextActionable?.id),
        subsections
    };
}
